import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const subscriptionManagementSlice = createSlice({
    name: "subscriptionManagement",
    initialState: {
        overview: null,
        plans: null,
        boostPackages: null,
        actionSuccess: false,
        errorDetailData: null,
        // Host subscriptions (list + detail)
        subscriptionPage: 0,
        subscriptionPageCount: 0,
        subscriptionTotalRow: 0,
        subscriptionList: null,
        subscriptionStats: null,
        subscriptionDetail: null,
        paymentPage: 0,
        paymentPageCount: 0,
        paymentTotalRow: 0,
        paymentList: null,
        exporting: false,
    },
    reducers: {
        doGetOverviewSuccess: (state, action) => {
            state.overview = action.payload.overview;
        },
        doGetOverviewFail: (state) => {
            state.overview = null;
        },
        doGetPlansSuccess: (state, action) => {
            state.plans = action.payload.data;
        },
        doGetPlansFail: (state) => {
            state.plans = null;
        },
        doGetBoostPackagesSuccess: (state, action) => {
            state.boostPackages = action.payload.data;
        },
        doGetBoostPackagesFail: (state) => {
            state.boostPackages = null;
        },
        doActionSuccess: (state) => {
            state.actionSuccess = true;
        },
        doActionFail: (state, action) => {
            state.actionSuccess = false;
            state.errorDetailData = action.payload;
        },
        doGetSubscriptionListSuccess: (state, action) => {
            state.subscriptionList = action.payload.data;
            state.subscriptionPage = action.payload.current_page;
            state.subscriptionPageCount = action.payload.page_count;
            state.subscriptionTotalRow = action.payload.total_row;
        },
        doGetSubscriptionListFail: (state) => {
            state.subscriptionList = null;
            state.subscriptionPage = 0;
            state.subscriptionPageCount = 0;
            state.subscriptionTotalRow = 0;
        },
        doGetSubscriptionStatsSuccess: (state, action) => {
            state.subscriptionStats = action.payload;
        },
        doGetSubscriptionStatsFail: (state) => {
            state.subscriptionStats = null;
        },
        doGetSubscriptionDetailSuccess: (state, action) => {
            state.subscriptionDetail = action.payload.subscription;
        },
        doGetSubscriptionDetailFail: (state) => {
            state.subscriptionDetail = null;
        },
        doGetSubscriptionPaymentsSuccess: (state, action) => {
            state.paymentList = action.payload.data;
            state.paymentPage = action.payload.current_page;
            state.paymentPageCount = action.payload.page_count;
            state.paymentTotalRow = action.payload.total_row;
        },
        doGetSubscriptionPaymentsFail: (state) => {
            state.paymentList = null;
            state.paymentPage = 0;
            state.paymentPageCount = 0;
            state.paymentTotalRow = 0;
        },
        setSubscriptionExporting: (state, action) => {
            state.exporting = action.payload;
        },
        resetSubscriptionState: (state) => {
            state.actionSuccess = false;
            state.errorDetailData = null;
        },
        resetSubscriptionDetailState: (state) => {
            state.subscriptionDetail = null;
            state.paymentList = null;
            state.paymentPage = 0;
            state.paymentPageCount = 0;
            state.paymentTotalRow = 0;
            state.actionSuccess = false;
            state.errorDetailData = null;
        },
    },
});

export const doGetOverview = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/admin/subscription/overview");
        dispatch(doGetOverviewSuccess(response.data));
    } catch (error) {
        dispatch(doGetOverviewFail(error.response));
    }
};

export const doGetPlans = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/admin/subscription/plans");
        dispatch(doGetPlansSuccess(response.data));
    } catch (error) {
        dispatch(doGetPlansFail(error.response));
    }
};

export const doCreatePlan = (payload) => async (dispatch) => {
    try {
        const response = await axios.post("/api/admin/subscription/plans", payload);
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

export const doUpdatePlan = (planId, payload) => async (dispatch) => {
    try {
        const response = await axios.put(`/api/admin/subscription/plans/${planId}`, payload);
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

export const doBulkUpdatePlans = (plans) => async (dispatch) => {
    try {
        const response = await axios.put("/api/admin/subscription/plans", { plans });
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

export const doUpdatePlanStatus = (planId, isActive) => async (dispatch) => {
    try {
        const response = await axios.patch(`/api/admin/subscription/plans/${planId}/status`, { is_active: isActive });
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

export const doGetBoostPackages = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/admin/subscription/boost-packages");
        dispatch(doGetBoostPackagesSuccess(response.data));
    } catch (error) {
        dispatch(doGetBoostPackagesFail(error.response));
    }
};

export const doCreateBoostPackage = (payload) => async (dispatch) => {
    try {
        const response = await axios.post("/api/admin/subscription/boost-packages", payload);
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

export const doUpdateBoostPackage = (packageId, payload) => async (dispatch) => {
    try {
        const response = await axios.put(`/api/admin/subscription/boost-packages/${packageId}`, payload);
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

export const doUpdateBoostPackageStatus = (packageId, isActive) => async (dispatch) => {
    try {
        const response = await axios.patch(`/api/admin/subscription/boost-packages/${packageId}/status`, { is_active: isActive });
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

// ---- host subscriptions ----------------------------------------------------

const buildSubscriptionListUrl = (param = {}) => {
    let url = `/api/admin/subscription/subscriptions?page=${param.page || 1}&size=${param.limit || 10}`;
    if (param.keyword) url += `&keyword=${encodeURIComponent(param.keyword)}`;
    if (param.plan_id && param.plan_id !== "ALL") url += `&plan_id=${param.plan_id}`;
    if (param.status) url += `&status=${param.status}`;
    if (param.date_from) url += `&date_from=${param.date_from}`;
    if (param.date_to) url += `&date_to=${param.date_to}`;
    if (param.sort_by) url += `&sort_by=${param.sort_by}`;
    return url;
};

export const doGetSubscriptionList = (param) => async (dispatch) => {
    try {
        const response = await axios.get(buildSubscriptionListUrl(param));
        dispatch(doGetSubscriptionListSuccess(response.data));
    } catch (error) {
        dispatch(doGetSubscriptionListFail(error.response));
    }
};

export const doGetSubscriptionStats = () => async (dispatch) => {
    try {
        const response = await axios.get("/api/admin/subscription/subscriptions/stats");
        dispatch(doGetSubscriptionStatsSuccess(response.data));
    } catch (error) {
        dispatch(doGetSubscriptionStatsFail(error.response));
    }
};

export const doGetSubscriptionDetail = (subscriptionId) => async (dispatch) => {
    try {
        const response = await axios.get(`/api/admin/subscription/subscriptions/${subscriptionId}`);
        dispatch(doGetSubscriptionDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetSubscriptionDetailFail(error.response));
    }
};

export const doGetSubscriptionPayments = (subscriptionId, param = {}) => async (dispatch) => {
    try {
        const url = `/api/admin/subscription/subscriptions/${subscriptionId}/payments?page=${param.page || 1}&size=${param.limit || 10}`;
        const response = await axios.get(url);
        dispatch(doGetSubscriptionPaymentsSuccess(response.data));
    } catch (error) {
        dispatch(doGetSubscriptionPaymentsFail(error.response));
    }
};

export const doUpdateSubscription = (subscriptionId, payload) => async (dispatch) => {
    try {
        const response = await axios.put(`/api/admin/subscription/subscriptions/${subscriptionId}`, payload);
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

export const doCancelSubscription = (subscriptionId, reason) => async (dispatch) => {
    try {
        const response = await axios.patch(`/api/admin/subscription/subscriptions/${subscriptionId}/cancel`, { reason: reason || "" });
        dispatch(doActionSuccess(response.data));
    } catch (error) {
        dispatch(doActionFail(error.response));
    }
};

// ---- CSV export ------------------------------------------------------------
// There is no server-side export endpoint, so the filtered set is pulled through
// the list endpoint (which caps `size` at 100) and the CSV is built in the
// browser — the same approach as the payout and deal monitoring exports.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 50;

const csvDate = (dt) => {
    if (!dt) return "";
    const d = new Date(dt);
    if (isNaN(d)) return "";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const STATUS_LABEL = {
    ACTIVE: "Active",
    EXPIRING_SOON: "Expiring Soon",
    EXPIRED: "Expired",
    CANCELLED: "Cancelled",
};

export const doExportSubscriptions = (param) => async (dispatch) => {
    try {
        dispatch(setSubscriptionExporting(true));

        const rows = [];
        for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
            const response = await axios.get(buildSubscriptionListUrl({ ...param, page, limit: EXPORT_PAGE_SIZE }));
            const batch = response.data?.data || [];
            rows.push(...batch);
            if (batch.length < EXPORT_PAGE_SIZE) break;
        }

        const header = [
            "Host ID", "Host", "Email", "Plan Type", "Status",
            "Start Date", "End Date", "Amount", "Payment Method", "Reference",
        ];
        const body = rows.map((r) => [
            r.host_id, r.host_name, r.host_email, r.plan_name,
            STATUS_LABEL[r.display_status] || r.display_status,
            csvDate(r.starts_at), csvDate(r.expires_at),
            r.amount, r.payment_method, r.reference_number,
        ]);
        const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");

        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        const now = new Date();
        link.setAttribute("download", `subscriptions_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setSubscriptionExporting(false));
    }
};

export const doExportSubscriptionPayments = (subscriptionId, hostCode) => async (dispatch) => {
    try {
        dispatch(setSubscriptionExporting(true));

        const rows = [];
        for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
            const url = `/api/admin/subscription/subscriptions/${subscriptionId}/payments?page=${page}&size=${EXPORT_PAGE_SIZE}`;
            const response = await axios.get(url);
            const batch = response.data?.data || [];
            rows.push(...batch);
            if (batch.length < EXPORT_PAGE_SIZE) break;
        }

        const header = [
            "Transaction ID", "Plan Name", "Start Date", "End Date", "Amount",
            "Payment Method", "Paid On", "Invoice",
        ];
        const body = rows.map((r) => [
            r.reference_number, r.plan_name,
            csvDate(r.starts_at), csvDate(r.expires_at), r.amount,
            r.payment_method, csvDate(r.paid_at), r.invoice_number,
        ]);
        const csv = [header, ...body].map((r) => r.map(csvCell).join(",")).join("\r\n");

        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.setAttribute("download", `payment_history_${hostCode || subscriptionId}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
        // no-op: keep the UI responsive even if the export fails
    } finally {
        dispatch(setSubscriptionExporting(false));
    }
};

export const {
    doGetOverviewSuccess, doGetOverviewFail,
    doGetPlansSuccess, doGetPlansFail,
    doGetBoostPackagesSuccess, doGetBoostPackagesFail,
    doGetSubscriptionListSuccess, doGetSubscriptionListFail,
    doGetSubscriptionStatsSuccess, doGetSubscriptionStatsFail,
    doGetSubscriptionDetailSuccess, doGetSubscriptionDetailFail,
    doGetSubscriptionPaymentsSuccess, doGetSubscriptionPaymentsFail,
    setSubscriptionExporting,
    doActionSuccess, doActionFail,
    resetSubscriptionState, resetSubscriptionDetailState,
} = subscriptionManagementSlice.actions;

export const getSubscriptionOverview = (state) => state.subscriptionManagement?.overview;
export const getSubscriptionPlans = (state) => state.subscriptionManagement?.plans;
export const getBoostPackages = (state) => state.subscriptionManagement?.boostPackages;
export const getSubscriptionActionSuccess = (state) => state.subscriptionManagement?.actionSuccess;
export const getSubscriptionErrorDetailData = (state) => state.subscriptionManagement?.errorDetailData;

export const getSubscriptionList = (state) => state.subscriptionManagement?.subscriptionList;
export const getSubscriptionListPage = (state) => state.subscriptionManagement?.subscriptionPage;
export const getSubscriptionListPageCount = (state) => state.subscriptionManagement?.subscriptionPageCount;
export const getSubscriptionListTotalRow = (state) => state.subscriptionManagement?.subscriptionTotalRow;
export const getSubscriptionStats = (state) => state.subscriptionManagement?.subscriptionStats;
export const getSubscriptionDetail = (state) => state.subscriptionManagement?.subscriptionDetail;
export const getSubscriptionPayments = (state) => state.subscriptionManagement?.paymentList;
export const getSubscriptionPaymentsPage = (state) => state.subscriptionManagement?.paymentPage;
export const getSubscriptionPaymentsPageCount = (state) => state.subscriptionManagement?.paymentPageCount;
export const getSubscriptionPaymentsTotalRow = (state) => state.subscriptionManagement?.paymentTotalRow;
export const getSubscriptionExporting = (state) => state.subscriptionManagement?.exporting;

export default subscriptionManagementSlice.reducer;
